import { BASE_URL } from '../../../shared/lib/api';
import { 
  FriendSummary, 
  FriendRequest, 
  FriendSearchResult, 
  FriendshipTier,
  FriendRequestsResponse
} from '../types/friends';

// Helper to get token from localStorage or context
// Since this is a standalone service file, we can't use hooks directly.
// We'll rely on the caller to pass the token or use a configured axios instance.
// However, the existing codebase seems to use fetch directly in some places or a custom wrapper.
// Looking at portfolioApi.ts, it takes a token as an argument.
// Looking at settingsApi.ts, it also takes a token.
// But the previous code used `apiClient`.
// Let's check `frontend/src/shared/lib/api.ts` again.
// It only exports `BASE_URL`. It does NOT export `apiClient`.

// So we need to implement these functions to take a token, similar to portfolioApi.ts and settingsApi.ts.

export async function fetchFriends(token: string, timeWindowKey: string): Promise<FriendSummary[]> {
  const res = await fetch(`${BASE_URL}/friends?time_window=${timeWindowKey}`, {
    headers: { 
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json'
    },
    credentials: 'include'
  });
  if (!res.ok) throw new Error('Failed to fetch friends');
  const results = await res.json();
  
  // Transform snake_case to camelCase
  return results.map((friend: any) => ({
    friendId: friend.friend_id,
    name: friend.name,
    username: friend.username,
    profilePictureUrl: friend.profile_picture_url,
    tierFromFriendToMe: friend.tier_from_friend_to_me,
    tierFromMeToFriend: friend.tier_from_me_to_friend,
    portfolioPercentChange: friend.portfolio_percent_change,
    lastPortfolioUpdate: friend.last_portfolio_update,
    unreadMessageCount: friend.unread_message_count || 0,
    friendshipId: friend.friendship_id
  }));
}

/**
 * Fetch basic friends list (id, name, username, avatar) without portfolio calculations.
 * Much faster than fetchFriends - used for chat modals and similar lightweight use cases.
 */
export async function fetchFriendsBasic(token: string): Promise<Pick<FriendSummary, 'friendId' | 'name' | 'username' | 'profilePictureUrl'>[]> {
  const res = await fetch(`${BASE_URL}/friends?basic=true`, {
    headers: { 
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json'
    },
    credentials: 'include'
  });
  if (!res.ok) throw new Error('Failed to fetch friends');
  const results = await res.json();
  
  // Transform snake_case to camelCase (only basic fields)
  return results.map((friend: any) => ({
    friendId: friend.friend_id,
    name: friend.name,
    username: friend.username,
    profilePictureUrl: friend.profile_picture_url
  }));
}

export async function searchUsers(token: string, query: string): Promise<FriendSearchResult[]> {
  const res = await fetch(`${BASE_URL}/friends/search?q=${encodeURIComponent(query)}`, {
    headers: { 
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json'
    },
    credentials: 'include'
  });
  
  if (!res.ok) throw new Error('Failed to search users');
  const results = await res.json();
  
  // Transform snake_case to camelCase
  return results.map((user: any) => ({
    userId: user.user_id,
    name: user.name,
    username: user.username,
    profilePictureUrl: user.profile_picture_url,
    isFriend: user.is_friend,
    hasOutgoingRequest: user.has_outgoing_request,
    hasIncomingRequest: user.has_incoming_request
  }));
}

export async function browseUsers(token: string, limit: number = 15): Promise<FriendSearchResult[]> {
  const res = await fetch(`${BASE_URL}/friends/browse?limit=${limit}`, {
    headers: { 
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json'
    },
    credentials: 'include'
  });
  
  if (!res.ok) throw new Error('Failed to browse users');
  const results = await res.json();
  
  // Transform snake_case to camelCase
  return results.map((user: any) => ({
    userId: user.user_id,
    name: user.name,
    username: user.username,
    profilePictureUrl: user.profile_picture_url,
    isFriend: user.is_friend,
    hasOutgoingRequest: user.has_outgoing_request,
    hasIncomingRequest: user.has_incoming_request
  }));
}

export interface UserProfile {
  userId: string;
  name: string;
  username: string | null;
  profilePictureUrl: string | null;
  isFriend: boolean;
  hasOutgoingRequest: boolean;
  hasIncomingRequest: boolean;
}

export async function getUserProfile(token: string, userId: string): Promise<UserProfile | null> {
  const res = await fetch(`${BASE_URL}/friends/users/${encodeURIComponent(userId)}/profile`, {
    headers: { 
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json'
    },
    credentials: 'include'
  });

  if (res.status === 404) return null;
  if (!res.ok) throw new Error('Failed to fetch user profile');

  const data = await res.json();
  return {
    userId: data.user_id,
    name: data.name,
    username: data.username,
    profilePictureUrl: data.profile_picture_url,
    isFriend: data.is_friend,
    hasOutgoingRequest: data.has_outgoing_request,
    hasIncomingRequest: data.has_incoming_request,
  };
}

export async function sendFriendRequest(token: string, args: {
  toUserId: string;
  tier: FriendshipTier;
  note?: string;
}): Promise<FriendRequest> {
  const res = await fetch(`${BASE_URL}/friends/requests`, {
    method: 'POST',
    headers: { 
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}` 
    },
    credentials: 'include',
    body: JSON.stringify({
      to_user_id: args.toUserId,
      tier: args.tier,
      note: args.note || ''
    })
  });
  
  if (!res.ok) throw new Error('Failed to send friend request');
  return res.json();
}

export async function fetchFriendRequests(token: string): Promise<FriendRequestsResponse> {
  const res = await fetch(`${BASE_URL}/friends/requests`, {
    headers: { 
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json'
    },
    credentials: 'include'
  });
  if (!res.ok) {
    const errorText = await res.text().catch(() => 'Unknown error');
    let errorMessage = 'Failed to fetch friend requests';
    try {
      const errorData = JSON.parse(errorText);
      errorMessage = errorData.error || errorMessage;
    } catch {
      // If parsing fails, use the status text or default message
      errorMessage = res.statusText || errorMessage;
    }
    throw new Error(errorMessage);
  }
  const results = await res.json();
  
  // Transform snake_case to camelCase
  const transformRequest = (req: any): FriendRequest => ({
    id: req.id,
    friendId: req.friend_id,
    name: req.name,
    username: req.username,
    profilePictureUrl: req.profile_picture_url,
    requestedTier: req.requested_tier,
    note: req.note,
    status: req.status,
    counterRequestedTier: req.counter_requested_tier,
    counterNote: req.counter_note,
    createdAt: req.created_at
  });
  
  return {
    incoming: results.incoming.map(transformRequest),
    outgoing: results.outgoing.map(transformRequest)
  };
}

export async function respondToFriendRequest(
  token: string,
  id: string,
  body:
    | { action: 'accept' }
    | { action: 'accept_lower'; tier: FriendshipTier }
    | { action: 'reject' }
    | { action: 'request_higher'; tier: FriendshipTier; note?: string }
): Promise<void> {
  const res = await fetch(`${BASE_URL}/friends/requests/${id}/respond`, {
    method: 'POST',
    headers: { 
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}` 
    },
    credentials: 'include',
    body: JSON.stringify(body)
  });
  if (!res.ok) throw new Error('Failed to respond to request');
}

export async function updateFriendshipTier(token: string, friendUserId: string, tier: FriendshipTier): Promise<void> {
  const res = await fetch(`${BASE_URL}/friends/${friendUserId}/tier`, {
    method: 'PUT',
    headers: { 
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json'
    },
    credentials: 'include',
    body: JSON.stringify({ tier })
  });
  if (!res.ok) throw new Error('Failed to update friendship tier');
}

export async function requestHigherTier(token: string, friendUserId: string, tier: FriendshipTier, note?: string): Promise<void> {
  const res = await fetch(`${BASE_URL}/friends/${friendUserId}/request-higher-tier`, {
    method: 'POST',
    headers: { 
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json'
    },
    credentials: 'include',
    body: JSON.stringify({ 
      tier, 
      note
    })
  });
  if (!res.ok) {
    const errorData = await res.json().catch(() => ({ error: 'Failed to request higher tier' }));
    const errorMessage = errorData.error || 'Failed to request higher tier';
    throw new Error(errorMessage);
  }
}

export async function unfriend(token: string, friendUserId: string): Promise<void> {
  const res = await fetch(`${BASE_URL}/friends/${friendUserId}`, {
    method: 'DELETE',
    headers: { 
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json'
    },
    credentials: 'include'
  });
  if (!res.ok) throw new Error('Failed to unfriend');
}

export type FriendDashboardResponse = {
  data: any; // Cache payload structure
  meta: {
    as_of_date: string;
    refreshed_at: string;
    time_window: string;
    allowed: boolean;
    tier: string;
  };
};

export async function fetchFriendPortfolio(token: string, friendId: string, timeWindowKey: string): Promise<FriendDashboardResponse> {
  const res = await fetch(`${BASE_URL}/friends/${friendId}/portfolio?time_window=${timeWindowKey}`, {
    headers: { 
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json'
    },
    credentials: 'include'
  });
  if (res.status === 403) throw new Error('Not friends or insufficient tier');
  if (!res.ok) throw new Error('Failed to fetch friend portfolio');
  return res.json();
}

export async function refreshFriendPortfolio(
  token: string,
  friendId: string,
  tier?: FriendshipTier): Promise<{ ok: boolean }> {
  /**
   * Refresh friend's portfolio data and return friend-specific cache payload.
   * 
   * SECURITY NOTE: The tier parameter is sent for convenience, but the backend
   * will always query the database for the actual tier and use that for all
   * data access decisions. This prevents privilege escalation through frontend manipulation.
   * 
   * @param token - JWT authentication token
   * @param friendId - ID of the friend whose portfolio to refresh
   * @param tier - Optional requested tier (for convenience, backend validates actual tier)
   * @returns Cache payload with friend-specific data (masked based on actual tier)
   */
  const body: any = {};
  if (tier) {
    body.tier = tier;
  }
  
  const res = await fetch(`${BASE_URL}/friends/${friendId}/portfolio/refresh`, {
    method: 'POST',
    headers: { 
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json'
    },
    credentials: 'include',
    body: JSON.stringify(body)
  });
  
  if (!res.ok) {
    const errorData = await res.json().catch(() => ({ error: 'Unknown error' }));
    throw new Error(errorData.error || 'Failed to refresh friend portfolio');
  }
  
  return res.json();
}
